import React from "react";
import { useNavigate } from "react-router-dom";
import { trackCTAClick } from "../../utils/analytics";
import stats from "../../data/atlasStats.json";
import "../../styles/Plan.css";

// The offer. Before this section existed the homepage made an excellent case
// for trusting Sârv and then never said what a subscription was, what it cost,
// or what it included — the CTA read "Get your plan" on a page that never
// defined a plan.
//
// The frame is honest and, as it happens, the strongest one available: the
// reference side of the site is genuinely free and stays free, so the paid
// pitch is not access to the library but the part that applies it to one
// person. Pretending the free material is locked would be both false and a
// worse argument.
//
// WORDING NOTE — read before editing the "any hour" item. That feature is
// automated. The copy here describes availability and speed and never claims a
// human is answering, because someone weighs a health answer differently if
// they believe a practitioner wrote it. Do not reword it toward "our team",
// "our experts", "ask a herbalist" or anything else that implies a person.
const PLAN_ITEMS = [
  {
    title: "A plan built around your body",
    body: "Your constitution, goals, medications and history decide what you are shown — not what is trending. Every suggestion is cross-checked for interactions before it reaches you.",
  },
  {
    title: "Start from a symptom",
    body: "Describe what you are actually feeling and work back to the plants with the best evidence behind them, filtered for what is safe alongside anything you already take.",
  },
  {
    title: "Find out what works for you",
    body: "Track how you respond over weeks. Herbs are slow and easy to misjudge from memory — a record tells you what to keep and what to drop.",
  },
  {
    title: "Answers at any hour",
    body: "Ask a health question at two in the morning and get a considered answer in seconds. No appointment, no waiting room, no charge per question.",
  },
  {
    title: "Habits that hold",
    body: "Sleep, energy, stress and routine tracked in one place, so a plan becomes something you follow rather than something you read once.",
  },
];

const PlanSection = () => {
  const navigate = useNavigate();
  const start = () => {
    trackCTAClick("Start free trial", "Homepage Plan Section");
    navigate("/start");
  };

  const herbs = (stats.herbs || 0).toLocaleString();

  return (
    <section className="sv-dark sv-section plan">
      <div className="sv-wrap">
        <p className="sv-eyebrow plan__eyebrow">Membership</p>
        <h2 className="sv-h2 sv-center plan__title">
          The evidence is free. The plan is what makes it yours.
        </h2>
        <p className="sv-body sv-center plan__intro">
          Every plant, condition and citation on this site stays free to read —
          all {herbs} of them. What a membership adds is the part a reference
          library cannot do on its own: work out which of it applies to you, and
          stay with you while you try it.
        </p>

        <ul className="plan__grid">
          {PLAN_ITEMS.map((item) => (
            <li key={item.title} className="sv-card plan__card">
              <h3 className="sv-h4 plan__card-title">{item.title}</h3>
              <p className="plan__card-body">{item.body}</p>
            </li>
          ))}
        </ul>

        <div className="plan__price">
          {/* Annual FIRST, deliberately. Leading with "$9.99 weekly" makes the
              reader annualise it to ~$520 before they reach the real number;
              leading with $1.35 a week anchors on the cheapest true framing and
              leaves weekly as the no-commitment option rather than the default.
              Same prices, same disclosure — only the order changed. */}
          <p className="plan__price-line">
            <strong>3 days free.</strong> Then $69.99 for the year — about{" "}
            <strong>$1.35 a week</strong>. Or $9.99 weekly if you would rather
            not commit.
          </p>
          <button type="button" className="sv-btn plan__cta" onClick={start}>
            Start your free trial
          </button>
          <p className="plan__price-note">
            Cancel any time during the trial and you are not charged.
          </p>
        </div>

        <p className="plan__disclaimer">
          Sârv supports general wellness and education. It is not a medical
          service, does not diagnose or treat any condition, and does not
          replace your doctor, midwife or pharmacist.
        </p>
      </div>
    </section>
  );
};

export default PlanSection;
